import { fetchApi, getAuthToken } from "./api";

// Admin base URL: derive from AUTH base URL
const AUTH_BASE =
  import.meta.env.VITE_AUTH_BASE_URL || "https://api.poolchemicaldata.online";
// Local: AUTH_BASE = "http://localhost/auth" → replace /auth with /admin
// Prod:  AUTH_BASE = "https://api.poolchemicaldata.online" → append /admin
const ADMIN_BASE =
  AUTH_BASE.endsWith("/auth") || AUTH_BASE.endsWith("/auth/")
    ? AUTH_BASE.replace(/\/auth\/?$/, "/admin")
    : `${AUTH_BASE.replace(/\/$/, "")}/admin`;

const TENANT_ADMIN_BASE =
  AUTH_BASE.endsWith("/auth") || AUTH_BASE.endsWith("/auth/")
    ? AUTH_BASE.replace(/\/auth\/?$/, "/tenant-admin")
    : `${AUTH_BASE.replace(/\/$/, "")}/tenant-admin`;

// ============ TYPES ============

export interface Tenant {
  id: string;
  name: string;
  nit: string | null;
  active: boolean;
  createdAt: string;
}

export interface TenantListResponse {
  tenants: Tenant[];
  total: number;
  message: string;
}

export interface CreateTenantRequest {
  name: string;
  nit?: string;
  active?: boolean;
}

export interface UpdateTenantRequest {
  name?: string;
  nit?: string;
  active?: boolean;
}

export interface CreateUserRequest {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  tenantId: string;
  roleId: number;
  technicianId?: number;
}

export interface TenantRole {
  id: number;
  name: string;
  description?: string;
}

export interface TenantManagedUser {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  emailVerified: boolean;
  technicianId?: number | null;
  tenantId: string;
  lastLoginAt?: string | null;
  createdAt: string;
  roleIds: number[];
  roleNames: string[];
}

export interface TenantAdminCreateUserRequest {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  roleId: number;
  technicianId?: number;
}

export interface TenantAdminUpdateUserRequest {
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  password?: string;
  isActive?: boolean;
  emailVerified?: boolean;
  technicianId?: number | null;
}

const getAuthHeaders = (): HeadersInit => {
  const token = getAuthToken();

  return {
    "Content-Type": "application/json",
    Accept: "application/json, text/plain",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const requestText = async (url: string, options: RequestInit = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...((options.headers as Record<string, string>) || {}),
    },
    mode: "cors",
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage: string;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.message || errorJson.error || errorText;
    } catch {
      errorMessage = errorText || `Error HTTP ${response.status}`;
    }
    throw new Error(errorMessage);
  }

  return response.text();
};

// ============ TENANT API ============

export const tenantApi = {
  getAll: () => fetchApi<TenantListResponse>(`${ADMIN_BASE}/tenants`),

  getActive: () => fetchApi<TenantListResponse>(`${ADMIN_BASE}/tenants/active`),

  getById: (id: string) => fetchApi<Tenant>(`${ADMIN_BASE}/tenants/${id}`),

  search: (name: string) =>
    fetchApi<TenantListResponse>(
      `${ADMIN_BASE}/tenants/search?name=${encodeURIComponent(name)}`,
    ),

  create: (data: CreateTenantRequest) =>
    fetchApi<Tenant>(`${ADMIN_BASE}/tenants`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: UpdateTenantRequest) =>
    fetchApi<Tenant>(`${ADMIN_BASE}/tenants/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  toggleStatus: (id: string) =>
    fetchApi<Tenant>(`${ADMIN_BASE}/tenants/${id}/toggle-status`, {
      method: "PATCH",
    }),

  delete: (id: string) =>
    fetchApi<void>(`${ADMIN_BASE}/tenants/${id}`, { method: "DELETE" }),
};

// ============ ADMIN USER API ============

export const adminUserApi = {
  createUser: async (data: CreateUserRequest): Promise<string> => {
    return requestText(`${ADMIN_BASE}/create-user`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },
};

export const tenantAdminApi = {
  getUsers: () => fetchApi<TenantManagedUser[]>(`${TENANT_ADMIN_BASE}/users`),

  getUserById: (userId: number) =>
    fetchApi<TenantManagedUser>(`${TENANT_ADMIN_BASE}/users/${userId}`),

  getRoles: () => fetchApi<TenantRole[]>(`${TENANT_ADMIN_BASE}/roles`),

  createUser: (data: TenantAdminCreateUserRequest) =>
    requestText(`${TENANT_ADMIN_BASE}/users`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateUser: (userId: number, data: TenantAdminUpdateUserRequest) =>
    requestText(`${TENANT_ADMIN_BASE}/users/${userId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  activateUser: (userId: number) =>
    requestText(`${TENANT_ADMIN_BASE}/users/${userId}/activate`, {
      method: "PUT",
    }),

  deactivateUser: (userId: number) =>
    requestText(`${TENANT_ADMIN_BASE}/users/${userId}/deactivate`, {
      method: "PUT",
    }),

  replaceUserRole: (userId: number, roleId: number) =>
    requestText(`${TENANT_ADMIN_BASE}/users/${userId}/role`, {
      method: "PUT",
      body: JSON.stringify({ userId, roleId }),
    }),
};
